import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plane, Heart, Trophy, Anchor, Sparkles, UserRound, Globe, CheckCircle, Shield, Map, Camera, Star, Sun, Play, ExternalLink, Instagram, ChevronRight, ChevronLeft, X, ZoomIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Plane, Heart, Trophy, Anchor, Sparkles, UserRound, Globe, CheckCircle, Shield, Map, Camera, Star, Sun
};

const HomePage = () => {
  const navigate = useNavigate();
  const [heroSlides, setHeroSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [slideInterval, setSlideInterval] = useState(6000); // Default 6 seconds
  const [philosophyData, setPhilosophyData] = useState({
    title: 'Our Philosophy',
    content: 'At Journeys N Tales, we believe travel is not just about destinations—it\'s about creating stories that last a lifetime.'
  });
  const [whyJntData, setWhyJntData] = useState({
    title: 'Why Choose JNT',
    subtitle: 'The JNT difference that sets us apart',
    reasons: [
      { icon: 'Shield', title: 'Trusted Expertise', description: 'Over 15 years of crafting unforgettable journeys' },
      { icon: 'Heart', title: 'Personalized Service', description: 'Every journey tailored to your unique preferences' },
      { icon: 'Globe', title: 'Global Network', description: 'Exclusive access to premium experiences worldwide' },
      { icon: 'Star', title: 'Award Winning', description: 'Recognized for excellence in luxury travel' }
    ]
  });
  const [ctaData, setCtaData] = useState({
    title: 'Ready to Begin Your Journey?',
    subtitle: 'Let our travel experts craft your perfect adventure',
    primaryText: 'Design My Journey',
    secondaryText: 'Contact Us'
  });
  const [experiences, setExperiences] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [travelGallery, setTravelGallery] = useState([]);
  const [featuredCountries, setFeaturedCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [currentGallerySet, setCurrentGallerySet] = useState(0);

  // Get all image URLs for lightbox navigation
  const galleryImages = travelGallery.filter(item => item.media_type !== 'video').map(item => item.media_url);
  
  const openLightbox = (imageUrl) => {
    const idx = galleryImages.indexOf(imageUrl);
    setLightboxImage(imageUrl);
    setLightboxIndex(idx >= 0 ? idx : 0);
    setLightboxOpen(true);
  };
  
  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage(null);
  };
  
  const lightboxPrev = (e) => {
    e.stopPropagation();
    const newIdx = lightboxIndex > 0 ? lightboxIndex - 1 : galleryImages.length - 1;
    setLightboxIndex(newIdx);
    setLightboxImage(galleryImages[newIdx]);
  };
  
  const lightboxNext = (e) => {
    e.stopPropagation();
    const newIdx = lightboxIndex < galleryImages.length - 1 ? lightboxIndex + 1 : 0;
    setLightboxIndex(newIdx);
    setLightboxImage(galleryImages[newIdx]);
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [heroRes, philosophyRes, homepageSettingsRes, experiencesRes, testimonialsRes, galleryRes, countriesRes] = await Promise.all([
          axios.get(`${API}/content/hero`).catch(() => null),
          axios.get(`${API}/content/philosophy`).catch(() => null),
          axios.get(`${API}/content/homepage-settings`).catch(() => null),
          axios.get(`${API}/experiences?featured_only=true`).catch(() => null),
          axios.get(`${API}/testimonials?featured_only=true`).catch(() => null),
          axios.get(`${API}/travel-gallery`).catch(() => null),
          axios.get(`${API}/countries/featured?limit=6`).catch(() => null)
        ]);

        // Build hero slides array - images first, then video for faster initial load
        const slides = [];
        const heroData = heroRes?.data || {};
        
        // Default slides if nothing configured
        const defaultImages = [
          'https://images.unsplash.com/photo-1551918120-9739cb430c6d',
          'https://images.unsplash.com/photo-1506905925346-21bda4d32df4',
          'https://images.unsplash.com/photo-1469474968028-56623f02e42e'
        ];
        
        // Add configured hero images
        if (heroData.hero_images && heroData.hero_images.length > 0) {
          heroData.hero_images.forEach(img => {
            slides.push({ type: 'image', url: img, loaded: false });
          });
        } else if (heroData.hero_image) {
          slides.push({ type: 'image', url: heroData.hero_image, loaded: false });
          // Add default additional images for carousel effect
          defaultImages.slice(1).forEach(img => {
            slides.push({ type: 'image', url: img, loaded: false });
          });
        } else {
          defaultImages.forEach(img => {
            slides.push({ type: 'image', url: img, loaded: false });
          });
        }
        
        // Add video last (loads after images)
        if (heroData.hero_video) {
          slides.push({ type: 'video', url: heroData.hero_video, loaded: false });
        }
        
        setHeroSlides(slides);
        setSlideInterval(heroData.slide_interval || 6000);
        
        // Store headline/subheadline separately
        setPhilosophyData(prev => ({
          ...prev,
          heroHeadline: heroData.headline || 'Journeys N Tales',
          heroSubheadline: heroData.subheadline || 'Bespoke Global Experiences, Curated Privately.',
          heroCtaText: heroData.cta_text || 'Design My Journey'
        }));
        
        setHeroLoaded(true);

        if (philosophyRes?.data) {
          setPhilosophyData(prev => ({
            ...prev,
            title: philosophyRes.data.title || prev.title,
            content: philosophyRes.data.content || prev.content
          }));
        }

        if (homepageSettingsRes?.data) {
          const settings = homepageSettingsRes.data;
          setWhyJntData({
            title: settings.why_jnt_title || whyJntData.title,
            subtitle: settings.why_jnt_subtitle || whyJntData.subtitle,
            reasons: settings.why_jnt_reasons || whyJntData.reasons
          });
          setCtaData({
            title: settings.cta_title || ctaData.title,
            subtitle: settings.cta_subtitle || ctaData.subtitle,
            primaryText: settings.cta_primary_text || ctaData.primaryText,
            secondaryText: settings.cta_secondary_text || ctaData.secondaryText
          });
        }

        if (experiencesRes?.data && experiencesRes.data.length > 0) {
          setExperiences(experiencesRes.data);
        }

        if (testimonialsRes?.data && testimonialsRes.data.length > 0) {
          setTestimonials(testimonialsRes.data);
        }

        if (galleryRes?.data && galleryRes.data.length > 0) {
          setTravelGallery(galleryRes.data);
        }

        if (countriesRes?.data && countriesRes.data.length > 0) {
          setFeaturedCountries(countriesRes.data);
        }
      } catch (error) {
        console.error('Error fetching content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  // Auto-scroll hero carousel
  useEffect(() => {
    if (heroSlides.length <= 1) return;
    
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    }, slideInterval);
    
    return () => clearInterval(timer);
  }, [heroSlides.length, slideInterval]);

  const handleCtaClick = () => {
    navigate('/design-journey');
  };

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % heroSlides.length);
  }, [heroSlides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, [heroSlides.length]);

  // Auto-scroll testimonials
  useEffect(() => {
    if (testimonials.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  // Auto-scroll gallery (every 8 seconds)
  useEffect(() => {
    if (travelGallery.length <= 5) return;
    const timer = setInterval(() => {
      setCurrentGallerySet(prev => {
        const maxSets = Math.ceil(travelGallery.length / 5) - 1;
        return prev >= maxSets ? 0 : prev + 1;
      });
    }, 8000);
    return () => clearInterval(timer);
  }, [travelGallery.length]);

  const nextTestimonial = () => {
    setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div className="page-container">
      {/* Hero Carousel Section */}
      <section className="hero-section hero-carousel">
        <div className="hero-overlay"></div>
        {heroLoaded && heroSlides.length > 0 && (
          <>
            {/* Carousel Slides */}
            {heroSlides.map((slide, index) => (
              <div 
                key={index}
                className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              >
                {slide.type === 'video' ? (
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="hero-video"
                    data-testid="hero-video"
                  >
                    <source src={slide.url} type="video/mp4" />
                  </video>
                ) : (
                  <img src={slide.url} alt="Luxury Travel" className="hero-image" />
                )}
              </div>
            ))}
            
            {/* Hero Content */}
            <div className="hero-content">
              <h1 className="hero-headline">{philosophyData.heroHeadline || 'Journeys N Tales'}</h1>
              <p className="hero-subheadline">{philosophyData.heroSubheadline || 'Bespoke Global Experiences, Curated Privately.'}</p>
              <Button onClick={handleCtaClick} className="hero-cta" data-testid="hero-cta">
                {philosophyData.heroCtaText || 'Design My Journey'}
              </Button>
            </div>

            {/* Carousel Navigation */}
            {heroSlides.length > 1 && (
              <>
                <button className="hero-nav hero-nav-prev" onClick={prevSlide} aria-label="Previous slide">
                  <ChevronLeft size={24} />
                </button>
                <button className="hero-nav hero-nav-next" onClick={nextSlide} aria-label="Next slide">
                  <ChevronRight size={24} />
                </button>
                <div className="hero-dots">
                  {heroSlides.map((_, index) => (
                    <button
                      key={index}
                      className={`hero-dot ${index === currentSlide ? 'active' : ''}`}
                      onClick={() => setCurrentSlide(index)}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
        {!heroLoaded && (
          <div className="hero-content" style={{ opacity: 0.7 }}>
            <div className="hero-loading-placeholder" style={{ minHeight: '100vh' }}></div>
          </div>
        )}
      </section>

      {/* Philosophy Section */}
      <section className="philosophy-section">
        <div className="section-container section-centered">
          <h2 className="section-title">{philosophyData.title}</h2>
          <p className="philosophy-text">{philosophyData.content}</p>
        </div>
      </section>

      {/* Featured Destinations (Countries) Section */}
      {featuredCountries.length > 0 && (
        <section className="featured-destinations-section" data-testid="featured-destinations-section">
          <div className="section-container section-centered">
            <h2 className="section-title">Featured Destinations</h2>
            <p className="section-subtitle">Explore our handpicked countries around the world</p>
            <div className="featured-countries-grid">
              {featuredCountries.slice(0, 6).map((country) => (
                <Link 
                  to={`/jnt-tales/country/${country.slug}`} 
                  key={country.id} 
                  className="featured-country-card"
                  data-testid={`featured-country-${country.id}`}
                >
                  <div className="featured-country-image">
                    {country.hero_image ? (
                      <img src={country.hero_image} alt={country.name} />
                    ) : (
                      <div className="featured-country-placeholder">
                        <Globe size={24} />
                      </div>
                    )}
                    <span className="featured-country-continent">{country.continent}</span>
                  </div>
                  <div className="featured-country-content">
                    <h3>{country.name}</h3>
                    <p>{country.description?.substring(0, 80)}...</p>
                    <span className="explore-link">Explore Country <ChevronRight size={16} /></span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="section-cta">
              <Link to="/jnt-tales" className="view-all-link" data-testid="view-all-destinations">
                Explore All Destinations <ChevronRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Signature Experiences Section */}
      {experiences.length > 0 && (
        <section className="experiences-section">
          <div className="section-container section-centered">
            <h2 className="section-title">Signature Experiences</h2>
            <p className="section-subtitle">Curated journeys designed for the extraordinary</p>
            <div className="experiences-grid">
              {experiences.map((experience) => {
                const IconComponent = iconMap[experience.icon];
                return (
                  <div key={experience.id} className="experience-card" data-testid={`experience-card-${experience.id}`}>
                    <div className="experience-image-container">
                      <img src={experience.image} alt={experience.title} className="experience-image" />
                      <div className="experience-icon-overlay">
                        {IconComponent && <IconComponent size={32} />}
                      </div>
                      {experience.instagram_link && (
                        <a 
                          href={experience.instagram_link} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="experience-instagram-icon"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`experience-instagram-${experience.id}`}
                        >
                          <Instagram size={16} />
                        </a>
                      )}
                    </div>
                    <div className="experience-content">
                      <h3 className="experience-title">{experience.title}</h3>
                      <p className="experience-description">{experience.description}</p>
                      <div className="experience-actions">
                        <Link to="/contact" className="experience-cta">Enquire Now</Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* JNT Precious Moments Gallery - Elegant Bento Layout with Carousel */}
      {travelGallery.length > 0 && (
        <section className="travel-gallery-section" data-testid="travel-gallery-section">
          <div className="section-container section-centered">
            <h2 className="section-title">JNT Precious Moments</h2>
            <p className="section-subtitle">Where every picture tells a tale</p>
            <div className="gallery-carousel-wrapper">
              {/* Navigation Arrows */}
              {travelGallery.length > 5 && (
                <button 
                  className="gallery-nav gallery-nav-prev" 
                  onClick={() => setCurrentGallerySet(prev => prev === 0 ? Math.ceil(travelGallery.length / 5) - 1 : prev - 1)}
                  aria-label="Previous gallery set"
                  data-testid="gallery-nav-prev"
                >
                  <ChevronLeft size={24} />
                </button>
              )}
              
              <div className="gallery-bento">
                {/* Main Large Image */}
                {(() => {
                  const startIndex = currentGallerySet * 5;
                  const currentItems = travelGallery.slice(startIndex, startIndex + 5);
                  const mainItem = currentItems[0];
                  
                  return mainItem && (
                    <div 
                      className="gallery-item gallery-item-main"
                      onClick={() => mainItem.media_type !== 'video' && openLightbox(mainItem.media_url)}
                      data-testid={`gallery-item-${mainItem.id}`}
                    >
                      {mainItem.media_type === 'video' ? (
                        <video src={mainItem.media_url} autoPlay loop muted playsInline controls style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <>
                          <img src={mainItem.media_url} alt={mainItem.caption || 'Travel moment'} />
                          <div className="gallery-zoom-icon"><ZoomIn size={24} /></div>
                        </>
                      )}
                      <div className="gallery-item-overlay">
                        {mainItem.caption && <p className="caption">{mainItem.caption}</p>}
                        {mainItem.destination && <span className="destination">{mainItem.destination}</span>}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Smaller Images Grid */}
                <div className="gallery-small-grid">
                  {(() => {
                    const startIndex = currentGallerySet * 5;
                    const currentItems = travelGallery.slice(startIndex + 1, startIndex + 5);
                    
                    return currentItems.map((item) => (
                      <div 
                        key={item.id} 
                        className={`gallery-item gallery-item-small ${item.media_type === 'video' ? 'video-item' : ''}`}
                        onClick={() => item.media_type !== 'video' && openLightbox(item.media_url)}
                        data-testid={`gallery-item-${item.id}`}
                      >
                        {item.media_type === 'video' ? (
                          <video src={item.media_url} autoPlay loop muted playsInline controls style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <>
                            <img src={item.media_url} alt={item.caption || 'Travel moment'} />
                            <div className="gallery-zoom-icon"><ZoomIn size={18} /></div>
                          </>
                        )}
                        <div className="gallery-item-overlay">
                          {item.caption && <p className="caption">{item.caption}</p>}
                          {item.destination && <span className="destination">{item.destination}</span>}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
              
              {/* Navigation Arrows */}
              {travelGallery.length > 5 && (
                <button 
                  className="gallery-nav gallery-nav-next" 
                  onClick={() => setCurrentGallerySet(prev => {
                    const maxSets = Math.ceil(travelGallery.length / 5) - 1;
                    return prev >= maxSets ? 0 : prev + 1;
                  })}
                  aria-label="Next gallery set"
                  data-testid="gallery-nav-next"
                >
                  <ChevronRight size={24} />
                </button>
              )}
            </div>
            
            {/* Gallery Dots */}
            {travelGallery.length > 5 && (
              <div className="gallery-dots">
                {Array.from({ length: Math.ceil(travelGallery.length / 5) }).map((_, index) => (
                  <button
                    key={index}
                    className={`gallery-dot ${index === currentGallerySet ? 'active' : ''}`}
                    onClick={() => setCurrentGallerySet(index)}
                    aria-label={`Go to gallery set ${index + 1}`}
                    data-testid={`gallery-dot-${index}`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Lightbox Modal with Navigation */}
      {lightboxOpen && lightboxImage && (
        <div className="lightbox-overlay" onClick={closeLightbox} data-testid="lightbox-overlay">
          <button className="lightbox-close" onClick={closeLightbox} data-testid="lightbox-close">
            <X size={32} />
          </button>
          {galleryImages.length > 1 && (
            <button className="lightbox-nav lightbox-prev" onClick={lightboxPrev} data-testid="lightbox-prev">
              <ChevronLeft size={36} />
            </button>
          )}
          <img src={lightboxImage} alt="Gallery view" className="lightbox-image" onClick={(e) => e.stopPropagation()} />
          {galleryImages.length > 1 && (
            <button className="lightbox-nav lightbox-next" onClick={lightboxNext} data-testid="lightbox-next">
              <ChevronRight size={36} />
            </button>
          )}
          <div className="lightbox-counter" style={{ position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: '14px', opacity: 0.8 }}>
            {lightboxIndex + 1} / {galleryImages.length}
          </div>
        </div>
      )}

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="testimonials-section" data-testid="testimonials-section">
          <div className="section-container section-centered">
            <h2 className="section-title">What Our Clients Say</h2>
            <p className="section-subtitle">Stories from travelers who trusted us with their journeys</p>
            <div className="testimonials-carousel-wrapper">
              {testimonials.length > 1 && (
                <button className="testimonial-nav testimonial-nav-prev" onClick={prevTestimonial} aria-label="Previous testimonial">
                  <ChevronLeft size={24} />
                </button>
              )}
              <div className="testimonials-carousel">
                {testimonials.map((testimonial, index) => (
                  <div 
                    key={testimonial.id} 
                    className={`testimonial-card ${index === currentTestimonial ? 'active' : ''}`}
                    style={{ display: index === currentTestimonial ? 'flex' : 'none' }}
                    data-testid={`testimonial-card-${testimonial.id}`}
                  >
                    {testimonial.video_url && !playingVideo ? (
                      <div className="testimonial-video-container">
                        <video 
                          src={testimonial.video_url} 
                          poster={testimonial.client_image}
                          controls
                          className="testimonial-video"
                        />
                      </div>
                    ) : (
                      <>
                        <div className="testimonial-quote">
                          <span className="quote-mark">"</span>
                          <p>{testimonial.quote}</p>
                        </div>
                        <div className="testimonial-rating">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} size={16} fill={i < testimonial.rating ? "#b8976c" : "none"} color="#b8976c" />
                          ))}
                        </div>
                      </>
                    )}
                    <div className="testimonial-author">
                      {testimonial.client_image && (
                        <img src={testimonial.client_image} alt={testimonial.client_name} className="author-image" />
                      )}
                      <div className="author-info">
                        <h4 className="author-name">{testimonial.client_name}</h4>
                        {testimonial.client_title && <p className="author-title">{testimonial.client_title}</p>}
                        {testimonial.destination && (
                          <p className="author-destination">Traveled to {testimonial.destination}</p>
                        )}
                      </div>
                      <div className="testimonial-badges">
                        {testimonial.video_url && (
                          <div className="video-badge">
                            <Play size={14} /> Video
                          </div>
                        )}
                        {testimonial.review_source && testimonial.review_link && (
                          <a 
                            href={testimonial.review_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`review-badge review-badge-${testimonial.review_source}`}
                            data-testid={`review-badge-${testimonial.review_source}`}
                          >
                            {testimonial.review_source === 'google' && (
                              <svg viewBox="0 0 24 24" width="14" height="14" style={{marginRight: '4px'}}>
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                              </svg>
                            )}
                            {testimonial.review_source === 'google' ? 'Google Review' : testimonial.review_source}
                            <ExternalLink size={10} style={{marginLeft: '4px'}} />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {testimonials.length > 1 && (
                <button className="testimonial-nav testimonial-nav-next" onClick={nextTestimonial} aria-label="Next testimonial">
                  <ChevronRight size={24} />
                </button>
              )}
            </div>
            {testimonials.length > 1 && (
              <div className="testimonial-dots">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    className={`testimonial-dot ${index === currentTestimonial ? 'active' : ''}`}
                    onClick={() => setCurrentTestimonial(index)}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Why JNT Section */}
      <section className="why-section">
        <div className="section-container section-centered">
          <h2 className="section-title">{whyJntData.title}</h2>
          <p className="section-subtitle">{whyJntData.subtitle}</p>
          <div className="why-grid">
            {whyJntData.reasons.map((reason, index) => {
              const IconComponent = iconMap[reason.icon];
              return (
                <div key={index} className="why-card">
                  <div className="why-icon">
                    {IconComponent && <IconComponent size={28} />}
                  </div>
                  <h3 className="why-title">{reason.title}</h3>
                  <p className="why-description">{reason.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="contact-cta-section">
        <div className="section-container section-centered">
          <h2 className="section-title">{ctaData.title}</h2>
          <p className="section-subtitle">{ctaData.subtitle}</p>
          <div className="cta-buttons">
            <Button onClick={handleCtaClick} className="primary-cta" data-testid="footer-cta">
              {ctaData.primaryText}
            </Button>
            <Link to="/contact" className="secondary-cta">
              {ctaData.secondaryText}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
